import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { usePlaidLink } from 'react-plaid-link';
import { 
  UserIcon, 
  PhotoIcon, 
  BellIcon, 
  CheckIcon,
  XMarkIcon,
  ArrowLeftIcon,
  SparklesIcon,
  LinkIcon,
  BanknotesIcon,
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  LinkSlashIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import AvatarPreferenceModal from './AvatarPreferenceModal';
import { LockedEmailSection } from './common/LockedEmailChannel';
import EngagementSettings from './common/EngagementSettings';
import SettingsGroup, { SettingsBlock } from './common/editorial/SettingsGroup';
import { FieldLabel, SegmentedControl } from './common/editorial/EditorialControls';
import UserAvatar from './common/UserAvatar';
import ImportPreviewTable from './common/ImportPreviewTable';
import CsvParsedPreview from './common/CsvParsedPreview';
import OcrParsedPreview from './common/OcrParsedPreview';
import DisconnectAccountModal from './DisconnectAccountModal';
import { useUpgrade } from '../context/UpgradeContext';
import { useCurrency, SUPPORTED_CURRENCY_LIST } from '../context/CurrencyContext';
import { useToast } from './common/Toast';
import ProBadge from './upgrade/ProBadge';

const AccountSettings = ({ user, onBack, onUserUpdate }) => {
  const { permissions, openUpgrade } = useUpgrade();
  const [profileName, setProfileName] = useState(user?.profile_name || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [newsletterFrequency, setNewsletterFrequency] = useState(user?.newsletter_frequency || 'weekly');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showDefaultAvatars, setShowDefaultAvatars] = useState(false);
  const [showAIAvatarModal, setShowAIAvatarModal] = useState(false);
  const [currencySaved, setCurrencySaved] = useState(false);
  const { currency, setCurrency } = useCurrency();
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const portfolioCsvFileRef = useRef(null);
  const accountsCsvFileRef = useRef(null);
  const portfolioImageFileRef = useRef(null);
  const accountsImageFileRef = useRef(null);

  // Connected accounts (bank/cashflow)
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualType, setManualType] = useState('checking');
  const [manualBalance, setManualBalance] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [accountsMessage, setAccountsMessage] = useState('');
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnectingPortfolio, setDisconnectingPortfolio] = useState(false);
  const [manualPortfolioType, setManualPortfolioType] = useState('text');
  const [manualPortfolioContent, setManualPortfolioContent] = useState('');
  const [manualPortfolioMessage, setManualPortfolioMessage] = useState('');
  const [aiCapabilities, setAiCapabilities] = useState(null);
  const [portfolioPreview, setPortfolioPreview] = useState(null);
  const [accountsImportText, setAccountsImportText] = useState('');
  const [accountsImportType, setAccountsImportType] = useState('text');
  const [accountsPreview, setAccountsPreview] = useState(null);
  const [portfolioCsvMeta, setPortfolioCsvMeta] = useState(null);
  const [accountsCsvMeta, setAccountsCsvMeta] = useState(null);
  const [portfolioCsvFile, setPortfolioCsvFile] = useState(null);
  const [accountsCsvFile, setAccountsCsvFile] = useState(null);
  const [portfolioImageFile, setPortfolioImageFile] = useState(null);
  const [accountsImageFile, setAccountsImageFile] = useState(null);
  const [portfolioOcrMeta, setPortfolioOcrMeta] = useState(null);
  const [accountsOcrMeta, setAccountsOcrMeta] = useState(null);
  const [importExtracting, setImportExtracting] = useState(false);
  const [importSaving, setImportSaving] = useState(false);

  // Default avatar options
  const defaultAvatars = [
    { id: 'default-1', url: '/avatars/default-1.svg', name: 'Gradient Circle' },
    { id: 'default-2', url: '/avatars/default-2.svg', name: 'Geometric' },
    { id: 'default-3', url: '/avatars/default-3.svg', name: 'Classic' },
    { id: 'default-4', url: '/avatars/default-4.svg', name: 'Abstract' },
    { id: 'default-5', url: '/avatars/default-5.svg', name: 'Minimal' },
    { id: 'default-6', url: '/avatars/default-6.svg', name: 'Friendly' },
  ];

  const newsletterOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'none', label: 'No Email Notifications' },
  ];

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const res = await axios.get('/api/cashflow/accounts');
      setAccounts(res.data?.accounts || []);
    } catch (e) {
      console.error('Failed to load accounts:', e);
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    axios
      .get('/api/ai/capabilities')
      .then((res) => {
        if (res.data?.capabilities) setAiCapabilities(res.data.capabilities);
      })
      .catch(() =>
        setAiCapabilities({
          supports_screenshot_import: false,
          supports_text_extraction: true,
        })
      );
  }, []);

  const supportsScreenshot = aiCapabilities?.supports_screenshot_import === true;
  const supportsVision = aiCapabilities?.supports_vision === true;

  const stripRowsForSave = (rows) =>
    (rows || []).map(({ uncertain_fields, ...rest }) => rest);

  const parseExtractError = (detail) => {
    if (typeof detail === 'string') {
      return { message: detail, csv_summary: null, ocr_summary: null };
    }
    if (detail && typeof detail === 'object') {
      return {
        message: detail.message || 'Could not extract data.',
        csv_summary: detail.csv_summary || null,
        ocr_summary: detail.ocr_summary || null,
      };
    }
    return { message: 'Could not extract data. Try CSV or shorter text.', csv_summary: null, ocr_summary: null };
  };

  const applyExtractResponse = (data, isPortfolio) => {
    const isImage = data.source === 'ocr' || data.source === 'vision';
    const csvMeta = !isImage
      ? {
          summary: data.csv_summary || null,
          stats: data.csv_stats || null,
          warnings: data.warnings || [],
          partial: data.partial === true,
        }
      : null;
    const ocrMeta = isImage
      ? {
          summary: data.ocr_summary || null,
          stats: data.ocr_stats || null,
          confidence: data.ocr_confidence,
          warnings: data.warnings || [],
          partial: data.partial === true,
          source: data.source,
        }
      : null;

    const statusMsg =
      (data.warnings?.length > 0 && data.warnings.join(' ')) ||
      (isImage
        ? 'Screenshot imported — confirm highlighted fields, then save.'
        : 'Review the parsed data and table below, then save.');

    if (isPortfolio) {
      setPortfolioCsvMeta(csvMeta);
      setPortfolioOcrMeta(ocrMeta);
      setPortfolioPreview(data.holdings || []);
      setManualPortfolioMessage(statusMsg);
    } else {
      setAccountsCsvMeta(csvMeta);
      setAccountsOcrMeta(ocrMeta);
      setAccountsPreview(data.accounts || []);
      setAccountsMessage(statusMsg);
    }
  };

  const handleExtractImport = async (importType) => {
    const isPortfolio = importType === 'portfolio';
    const mode = isPortfolio ? manualPortfolioType : accountsImportType;
    const isCsvMode = mode === 'csv';
    const isScreenshotMode = mode === 'screenshot';
    const text = isPortfolio ? manualPortfolioContent.trim() : accountsImportText.trim();
    const csvFile = isPortfolio ? portfolioCsvFile : accountsCsvFile;
    const imageFile = isPortfolio ? portfolioImageFile : accountsImageFile;

    if (isScreenshotMode && !imageFile) {
      const msg = 'Upload a PNG or JPG screenshot.';
      if (isPortfolio) setManualPortfolioMessage(msg);
      else setAccountsMessage(msg);
      return;
    }
    if (isCsvMode && !csvFile && !text) {
      const msg = 'Upload a .csv file or paste CSV content.';
      if (isPortfolio) setManualPortfolioMessage(msg);
      else setAccountsMessage(msg);
      return;
    }
    if (!isCsvMode && !isScreenshotMode && !text) {
      const msg = 'Paste your holdings or accounts first.';
      if (isPortfolio) setManualPortfolioMessage(msg);
      else setAccountsMessage(msg);
      return;
    }

    setImportExtracting(true);
    if (isPortfolio) {
      setManualPortfolioMessage('');
      setPortfolioCsvMeta(null);
      setPortfolioOcrMeta(null);
    } else {
      setAccountsMessage('');
      setAccountsCsvMeta(null);
      setAccountsOcrMeta(null);
    }

    try {
      let res;
      if (isScreenshotMode && imageFile) {
        const form = new FormData();
        form.append('file', imageFile);
        form.append('import_type', importType);
        res = await axios.post('/api/import/extract-image', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (isCsvMode && csvFile) {
        const form = new FormData();
        form.append('file', csvFile);
        form.append('import_type', importType);
        res = await axios.post('/api/import/extract-csv', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await axios.post('/api/import/extract', {
          text,
          import_type: importType,
          source: isCsvMode ? 'csv' : 'text',
        });
      }
      applyExtractResponse(res.data, isPortfolio);
    } catch (e) {
      const { message, csv_summary, ocr_summary } = parseExtractError(e.response?.data?.detail);
      if (isPortfolio) {
        setPortfolioCsvMeta(
          csv_summary
            ? { summary: csv_summary, stats: null, warnings: [message], partial: true }
            : null
        );
        setPortfolioOcrMeta(
          ocr_summary
            ? { summary: ocr_summary, stats: null, warnings: [message], partial: true, source: 'ocr' }
            : null
        );
        setPortfolioPreview(null);
        setManualPortfolioMessage(message);
      } else {
        setAccountsCsvMeta(
          csv_summary
            ? { summary: csv_summary, stats: null, warnings: [message], partial: true }
            : null
        );
        setAccountsOcrMeta(
          ocr_summary
            ? { summary: ocr_summary, stats: null, warnings: [message], partial: true, source: 'ocr' }
            : null
        );
        setAccountsPreview(null);
        setAccountsMessage(message);
      }
    } finally {
      setImportExtracting(false);
    }
  };

  const handleSaveImport = async (importType) => {
    const isPortfolio = importType === 'portfolio';
    const rows = isPortfolio ? portfolioPreview : accountsPreview;
    if (!rows?.length) return;

    setImportSaving(true);
    try {
      const cleanRows = stripRowsForSave(rows);
      const payload = isPortfolio
        ? { import_type: 'portfolio', holdings: cleanRows, replace_existing: true }
        : { import_type: 'accounts', accounts: cleanRows };
      const res = await axios.post('/api/import/save', payload);
      if (isPortfolio) {
        setPortfolioPreview(null);
        setPortfolioCsvMeta(null);
        setPortfolioOcrMeta(null);
        setManualPortfolioContent('');
        if (portfolioCsvFileRef.current) portfolioCsvFileRef.current.value = '';
        if (portfolioImageFileRef.current) portfolioImageFileRef.current.value = '';
        setPortfolioCsvFile(null);
        setPortfolioImageFile(null);
        setManualPortfolioMessage(res.data.message || 'Portfolio saved.');
        onUserUpdate?.({ ...user, portfolio_connected: true, onboarding_complete: true });
      } else {
        setAccountsPreview(null);
        setAccountsCsvMeta(null);
        setAccountsOcrMeta(null);
        setAccountsImportText('');
        if (accountsCsvFileRef.current) accountsCsvFileRef.current.value = '';
        if (accountsImageFileRef.current) accountsImageFileRef.current.value = '';
        setAccountsCsvFile(null);
        setAccountsImageFile(null);
        setAccountsMessage('Accounts saved.');
        await loadAccounts();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const err = typeof detail === 'string' ? detail : 'Save failed.';
      if (isPortfolio) setManualPortfolioMessage(err);
      else setAccountsMessage(err);
    } finally {
      setImportSaving(false);
    }
  };

  const getPlaidLinkToken = async () => {
    if (!permissions?.can_connect_accounts) {
      openUpgrade('connect_accounts');
      return;
    }
    setPlaidLoading(true);
    setAccountsMessage('');
    try {
      const res = await axios.post('/api/plaid/create_cashflow_link_token');
      setLinkToken(res.data?.link_token || null);
    } catch (e) {
      console.error('Plaid link token error:', e);
      setAccountsMessage('Could not start Plaid. Please try again.');
      setPlaidLoading(false);
    }
  };

  const onPlaidSuccess = useCallback(async (publicToken) => {
    setPlaidLoading(true);
    setAccountsMessage('');
    try {
      await axios.post('/api/plaid/exchange_cashflow_token', {
        public_token: publicToken,
        user_id: user?.user_id,
      });
      await loadAccounts();
      setAccountsMessage('Account(s) connected successfully.');
      setLinkToken(null);
    } catch (e) {
      console.error('Plaid exchange error:', e);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && typeof detail === 'object' && detail?.error === 'connect_accounts_gated') {
        openUpgrade('connect_accounts');
        setAccountsMessage('');
      } else {
        setAccountsMessage('Failed to add account. Please try again.');
      }
    } finally {
      setPlaidLoading(false);
    }
  }, [user?.user_id, loadAccounts, openUpgrade]);

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: () => {
      setPlaidLoading(false);
      setLinkToken(null);
    },
  });

  useEffect(() => {
    if (linkToken && plaidReady) openPlaid();
  }, [linkToken, plaidReady, openPlaid]);

  const formatLastSynced = (iso) => {
    if (!iso) return 'Never synced';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  const handleRequestDisconnect = (acc) => {
    setDisconnectTarget(acc);
  };

  const handleConfirmDisconnect = async (acc) => {
    const accountId = acc.account_id;
    setDisconnectingId(accountId);
    try {
      if (acc.plaid_item_id) {
        await axios.post(`/api/accounts/${encodeURIComponent(accountId)}/disconnect`);
      } else {
        await axios.delete(`/api/cashflow/accounts/${encodeURIComponent(accountId)}`);
      }
      setAccountsMessage('Account disconnected.');
      setDisconnectTarget(null);
      await loadAccounts();
    } catch (e) {
      setAccountsMessage(e.response?.data?.detail || 'Failed to disconnect account.');
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleDisconnectPortfolio = async () => {
    if (!window.confirm('Disconnect your Plaid portfolio? You can add holdings manually or reconnect later in Account Settings.')) return;
    setDisconnectingPortfolio(true);
    try {
      await axios.post('/api/plaid/disconnect-portfolio');
      onUserUpdate?.({ ...user, portfolio_connected: false });
      setMessage('Portfolio disconnected. You can reconnect or add accounts manually.');
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Failed to disconnect portfolio.');
    } finally {
      setDisconnectingPortfolio(false);
    }
  };


  const handleAddManualAccount = async (e) => {
    e.preventDefault();
    const name = (manualName || '').trim();
    if (!name) {
      setAccountsMessage('Please enter an account name.');
      return;
    }
    const balance = parseFloat(manualBalance);
    if (isNaN(balance) || balance < 0) {
      setAccountsMessage('Please enter a valid balance (0 or more).');
      return;
    }
    setManualSaving(true);
    setAccountsMessage('');
    try {
      await axios.post('/api/cashflow/accounts', [{
        account_name: name,
        type: manualType,
        balance: balance,
        institution_name: 'Manual',
      }]);
      await loadAccounts();
      setShowManualForm(false);
      setManualName('');
      setManualBalance('');
      setAccountsMessage('Account added successfully.');
    } catch (e) {
      console.error('Add manual account error:', e);
      setAccountsMessage(e.response?.data?.detail || 'Failed to add account.');
    } finally {
      setManualSaving(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await axios.put('/api/user/profile', {
        profile_name: profileName,
        avatar_url: avatarUrl,
        newsletter_frequency: newsletterFrequency,
      });

      if (response.data.status === 'success' || response.data.success) {
        setMessage('✅ Profile updated successfully!');
        
        // Create updated user object
        const updatedUser = {
          ...user,
          profile_name: profileName,
          full_name: profileName, // Update full_name too for dashboard greeting
          avatar_url: avatarUrl,
          newsletter_frequency: newsletterFrequency,
        };
        
        // Update user context with new data
        if (onUserUpdate) {
          onUserUpdate(updatedUser);
        }
        
        // Also update localStorage if being used
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            localStorage.setItem('user', JSON.stringify({
              ...parsed,
              profile_name: profileName,
              full_name: profileName,
              avatar_url: avatarUrl,
              newsletter_frequency: newsletterFrequency,
            }));
          } catch (e) {
          }
        }
        
        // Redirect to dashboard after showing success message
        setTimeout(() => {
          if (onBack) {
            onBack(); // Use onBack callback if available
          } else {
            window.location.href = '/dashboard';
          }
        }, 1500);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to update profile. Please try again.';
      setMessage(`❌ ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // For now, we'll use a placeholder URL
      // In production, you'd upload to a cloud service
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDefaultAvatarSelect = (avatar) => {
    setAvatarUrl(avatar.url);
    setShowDefaultAvatars(false);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const displayName = profileName || user?.full_name || user?.email || 'User';

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-3xl mx-auto px-6 py-8 md:py-10">
        <motion.header
          className="flex items-center gap-4 mb-10 pb-6 border-b border-hairline"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <button type="button" onClick={onBack} className="btn-ghost p-2" aria-label="Back">
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="font-display text-h1 text-primary">Account settings</h1>
        </motion.header>

        <div className="space-y-2">
            <SettingsGroup
              title="Profile name"
              description="Used in greetings and throughout the app."
              icon={UserIcon}
            >
              <SettingsBlock>
                <FieldLabel htmlFor="profile-name">Display name</FieldLabel>
                <input
                  id="profile-name"
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Enter your profile name"
                  className="input-editorial max-w-md"
                />
              </SettingsBlock>
            </SettingsGroup>

            <SettingsGroup
              title="Avatar"
              description="Upload a photo or use your initials avatar."
              icon={PhotoIcon}
            >
              <SettingsBlock>
                <div className="flex items-center gap-6 mb-4">
                  {avatarUrl ? (
                    <UserAvatar
                      userId={user?.user_id}
                      src={avatarUrl}
                      alt="Avatar"
                      size="md"
                      rounded="lg"
                    />
                  ) : (
                    <div
                      className="w-20 h-20 rounded-full bg-pine flex items-center justify-center border border-hairline shrink-0"
                      aria-hidden="true"
                    >
                      <span className="font-display text-h2 text-bone-raised">
                        {getInitials(displayName)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-body text-primary font-medium">Current avatar</p>
                    <p className="text-label text-secondary">
                      {avatarUrl
                        ? 'Custom photo — shown in the header and across the app'
                        : 'Initials avatar — shown in the header and across the app'}
                    </p>
                  </div>
                </div>
                {!avatarUrl && (
                  <p className="text-label text-ink-muted mb-4">
                    AI Avatar generation is temporarily unavailable. Your initials avatar will be
                    used for now.
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-secondary flex items-center gap-2">
                    <PhotoIcon className="w-5 h-5" />
                    Upload photo
                  </button>
                  <button type="button" onClick={() => setShowDefaultAvatars(!showDefaultAvatars)} className="btn-ghost flex items-center gap-2">
                    <UserIcon className="w-5 h-5" />
                    Choose default
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                </div>
                {showDefaultAvatars && (
                  <div className="mt-6 pt-4 border-t border-hairline">
                    <p className="text-label text-secondary mb-3">Default avatars</p>
                    <div className="grid grid-cols-6 gap-3">
                      {defaultAvatars.map((avatar) => (
                        <button
                          key={avatar.id}
                          type="button"
                          onClick={() => handleDefaultAvatarSelect(avatar)}
                          className="w-12 h-12 rounded-lg overflow-hidden border border-hairline hover:border-pine transition-colors duration-300"
                          title={avatar.name}
                        >
                          <img src={avatar.url} alt={avatar.name} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </SettingsBlock>
            </SettingsGroup>

            {user?.portfolio_connected && (
              <div className="settings-danger-zone">
                <SettingsGroup
                  title="Portfolio connection"
                  description="Connected via Plaid. Disconnect to add holdings manually instead."
                  icon={LinkIcon}
                >
                  <SettingsBlock>
                    <button
                      type="button"
                      onClick={handleDisconnectPortfolio}
                      disabled={disconnectingPortfolio}
                      className="btn-danger flex items-center gap-2"
                    >
                      <LinkSlashIcon className="w-5 h-5" />
                      {disconnectingPortfolio ? 'Disconnecting…' : 'Disconnect portfolio'}
                    </button>
                  </SettingsBlock>
                </SettingsGroup>
              </div>
            )}

            <SettingsGroup
              title="Add brokerage manually"
              description="Paste holdings, upload CSV, or import a screenshot. Review the preview before saving."
              icon={PencilSquareIcon}
            >
              <SettingsBlock>
              <SegmentedControl
                options={[
                  { value: 'text', label: 'Describe in text' },
                  { value: 'csv', label: 'CSV file' },
                  ...(supportsScreenshot ? [{ value: 'screenshot', label: 'Screenshot' }] : []),
                ]}
                value={manualPortfolioType}
                onChange={(v) => {
                  setManualPortfolioType(v);
                  setManualPortfolioMessage('');
                  setPortfolioPreview(null);
                  setPortfolioCsvMeta(null);
                  setPortfolioOcrMeta(null);
                }}
              />
              <div className="mt-4">
              {manualPortfolioType === 'screenshot' && supportsScreenshot && (
                <div className="mb-3">
                  <input
                    ref={portfolioImageFileRef}
                    type="file"
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    onChange={(e) => {
                      setPortfolioImageFile(e.target.files?.[0] || null);
                      setManualPortfolioMessage('');
                      setPortfolioPreview(null);
                      setPortfolioOcrMeta(null);
                    }}
                    className="file-input-editorial"
                    disabled={importExtracting || importSaving}
                  />
                  <p className="text-label text-faint mt-1">
                    {supportsVision
                      ? 'PNG/JPG screenshot — read via OCR with vision fallback when enabled.'
                      : 'PNG/JPG screenshot — text is read on the server (OCR), then structured for preview.'}
                  </p>
                </div>
              )}
              {manualPortfolioType === 'csv' && (
                <div className="mb-3">
                  <input
                    ref={portfolioCsvFileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      setPortfolioCsvFile(e.target.files?.[0] || null);
                      setManualPortfolioMessage('');
                      setPortfolioPreview(null);
                      setPortfolioCsvMeta(null);
                    }}
                    className="file-input-editorial"
                    disabled={importExtracting || importSaving}
                  />
                  <p className="text-label text-faint mt-1">
                    Upload a brokerage export (.csv), or paste CSV below. We parse it on the server before extraction.
                  </p>
                </div>
              )}
              {!supportsScreenshot && (
                <p className="text-label text-faint mb-3 border border-hairline rounded-md px-3 py-2 bg-bone-raised">
                  Screenshots require server OCR (install Tesseract) or AI_SUPPORTS_VISION — use text or CSV.
                </p>
              )}
              {manualPortfolioType !== 'screenshot' && (
              <textarea
                value={manualPortfolioContent}
                onChange={(e) => {
                  setManualPortfolioContent(e.target.value);
                  setManualPortfolioMessage('');
                  setPortfolioPreview(null);
                  setPortfolioCsvMeta(null);
                }}
                placeholder={
                  manualPortfolioType === 'text'
                    ? 'e.g. I hold Microsoft, NVIDIA, AMD... Share counts optional.'
                    : 'Or paste CSV: symbol,name,shares,value\nVTI,Vanguard Total Stock,10,2450'
                }
                className="input-editorial resize-none font-mono text-label mt-3"
                rows={5}
                disabled={importExtracting || importSaving}
              />
              )}
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => handleExtractImport('portfolio')}
                  disabled={
                    importExtracting ||
                    importSaving ||
                    (manualPortfolioType === 'screenshot'
                      ? !portfolioImageFile
                      : manualPortfolioType === 'csv'
                        ? !manualPortfolioContent.trim() && !portfolioCsvFile
                        : !manualPortfolioContent.trim())
                  }
                  className="btn-secondary disabled:opacity-50"
                >
                  {importExtracting ? 'Extracting…' : 'Preview import'}
                </button>
                {manualPortfolioMessage && (
                  <span
                    className={`text-label ${manualPortfolioMessage.includes('saved') || manualPortfolioMessage.includes('Saved') ? 'text-pine' : 'text-warning'}`}
                  >
                    {manualPortfolioMessage}
                  </span>
                )}
              </div>
              {portfolioCsvMeta?.summary && (
                <CsvParsedPreview
                  summary={portfolioCsvMeta?.summary}
                  stats={portfolioCsvMeta?.stats}
                  warnings={portfolioCsvMeta?.warnings}
                  partial={portfolioCsvMeta?.partial}
                />
              )}
              {portfolioOcrMeta?.summary && (
                <OcrParsedPreview
                  summary={portfolioOcrMeta?.summary}
                  stats={portfolioOcrMeta?.stats}
                  confidence={portfolioOcrMeta?.confidence}
                  warnings={portfolioOcrMeta?.warnings}
                  partial={portfolioOcrMeta?.partial}
                  source={portfolioOcrMeta?.source}
                />
              )}
              {portfolioPreview && (
                <ImportPreviewTable
                  importType="portfolio"
                  rows={portfolioPreview}
                  onChange={setPortfolioPreview}
                  onSave={() => handleSaveImport('portfolio')}
                  onCancel={() => {
                    setPortfolioPreview(null);
                    setPortfolioCsvMeta(null);
                    setManualPortfolioMessage('');
                  }}
                  saving={importSaving}
                  saveLabel="Save portfolio"
                />
              )}
              </div>
              </SettingsBlock>
            </SettingsGroup>

            <SettingsGroup
              title="Connected accounts"
              description="Bank and cash accounts for cashflow. Connect via Plaid, import with preview, or add manually."
              icon={BanknotesIcon}
            >
              <SettingsBlock>
                <p className="text-label text-secondary mb-3">Import from text or CSV</p>
                <SegmentedControl
                  options={[
                    { value: 'text', label: 'Free text' },
                    { value: 'csv', label: 'CSV' },
                    ...(supportsScreenshot ? [{ value: 'screenshot', label: 'Screenshot' }] : []),
                  ]}
                  value={accountsImportType}
                  onChange={(v) => {
                    setAccountsImportType(v);
                    setAccountsPreview(null);
                    setAccountsCsvMeta(null);
                    setAccountsOcrMeta(null);
                  }}
                />
                <div className="mt-4">
                {accountsImportType === 'screenshot' && supportsScreenshot && (
                  <input
                    ref={accountsImageFileRef}
                    type="file"
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    onChange={(e) => {
                      setAccountsImageFile(e.target.files?.[0] || null);
                      setAccountsPreview(null);
                      setAccountsOcrMeta(null);
                    }}
                    className="file-input-editorial mb-2"
                    disabled={importExtracting || importSaving}
                  />
                )}
                {accountsImportType === 'csv' && (
                  <input
                    ref={accountsCsvFileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      setAccountsCsvFile(e.target.files?.[0] || null);
                      setAccountsPreview(null);
                      setAccountsCsvMeta(null);
                    }}
                    className="file-input-editorial mb-2"
                    disabled={importExtracting || importSaving}
                  />
                )}
                {accountsImportType !== 'screenshot' && (
                <textarea
                  value={accountsImportText}
                  onChange={(e) => {
                    setAccountsImportText(e.target.value);
                    setAccountsPreview(null);
                    setAccountsCsvMeta(null);
                  }}
                  placeholder={
                    accountsImportType === 'csv'
                      ? 'name,type,balance,institution\nChase Checking,checking,2500,Chase'
                      : 'e.g. Chase checking $2,500, Marcus savings $10,000'
                  }
                  className="input-editorial font-mono text-label resize-none"
                  rows={3}
                />
                )}
                <button
                  type="button"
                  onClick={() => handleExtractImport('accounts')}
                  disabled={
                    importExtracting ||
                    importSaving ||
                    (accountsImportType === 'screenshot'
                      ? !accountsImageFile
                      : accountsImportType === 'csv'
                        ? !accountsImportText.trim() && !accountsCsvFile
                        : !accountsImportText.trim())
                  }
                  className="btn-secondary mt-3 disabled:opacity-50"
                >
                  {importExtracting ? 'Extracting…' : 'Preview import'}
                </button>
                {accountsCsvMeta?.summary && (
                  <CsvParsedPreview
                    summary={accountsCsvMeta?.summary}
                    stats={accountsCsvMeta?.stats}
                    warnings={accountsCsvMeta?.warnings}
                    partial={accountsCsvMeta?.partial}
                  />
                )}
                {accountsOcrMeta?.summary && (
                  <OcrParsedPreview
                    summary={accountsOcrMeta?.summary}
                    stats={accountsOcrMeta?.stats}
                    confidence={accountsOcrMeta?.confidence}
                    warnings={accountsOcrMeta?.warnings}
                    partial={accountsOcrMeta?.partial}
                    source={accountsOcrMeta?.source}
                  />
                )}
                {accountsPreview && (
                  <ImportPreviewTable
                    importType="accounts"
                    rows={accountsPreview}
                    onChange={setAccountsPreview}
                    onSave={() => handleSaveImport('accounts')}
                    onCancel={() => setAccountsPreview(null)}
                    saving={importSaving}
                    saveLabel="Save accounts"
                  />
                )}
              </div>
              </SettingsBlock>

              <SettingsBlock className="border-t border-hairline">
              {accountsLoading ? (
                <p className="text-body text-secondary">Loading accounts…</p>
              ) : accounts.length > 0 ? (
                <div className="hairline-list space-y-0">
                  {accounts.map((acc) => (
                    <div
                      key={acc.account_id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4 border-b border-hairline last:border-0"
                    >
                      <div>
                        <p className="text-body font-medium text-primary">
                          {acc.institution_name && acc.institution_name !== 'Manual'
                            ? acc.institution_name
                            : acc.account_name || 'Unnamed account'}
                        </p>
                        <p className="text-label text-secondary capitalize">
                          {(acc.type || 'checking').replace('_', ' ')}
                          {acc.plaid_item_id && <span className="ml-2 text-pine">Connected</span>}
                        </p>
                        <p className="text-label text-secondary mt-1">
                          Last synced: {formatLastSynced(acc.synced_at || acc.updated_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <p className="figure text-body text-primary">
                          ${typeof acc.balance === 'number'
                            ? acc.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })
                            : Number(acc.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                        {acc.plaid_item_id ? (
                          <button
                            type="button"
                            onClick={() => handleRequestDisconnect(acc)}
                            disabled={disconnectingId === acc.account_id}
                            className="btn-secondary text-danger border-danger/40 disabled:opacity-50"
                          >
                            Disconnect
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRequestDisconnect(acc)}
                            disabled={disconnectingId === acc.account_id}
                            className="btn-ghost p-2 text-danger disabled:opacity-50"
                            title="Remove manual account"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-body text-secondary">No accounts connected yet.</p>
              )}

              {accountsMessage && (
                <p className={`text-label mt-3 ${accountsMessage.includes('success') || accountsMessage.includes('connected') ? 'text-pine' : 'text-warning'}`}>
                  {accountsMessage}
                </p>
              )}

              <div className="flex flex-wrap gap-3 mt-4">
                <button type="button" onClick={getPlaidLinkToken} disabled={plaidLoading} className="btn-primary flex items-center gap-2">
                  <LinkIcon className="w-5 h-5" />
                  {plaidLoading ? 'Connecting…' : 'Connect with Plaid'}
                  {!permissions?.can_connect_accounts && <ProBadge feature="connect_accounts" />}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowManualForm(!showManualForm); setAccountsMessage(''); }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <PlusCircleIcon className="w-5 h-5" />
                  Add manually
                </button>
              </div>

              {showManualForm && (
                <form className="mt-6 pt-4 border-t border-hairline space-y-4" onSubmit={handleAddManualAccount}>
                  <p className="text-body text-primary font-medium flex items-center gap-2">
                    <PencilSquareIcon className="w-4 h-4 text-pine" />
                    Add account manually
                  </p>
                  <div>
                    <FieldLabel htmlFor="manual-name">Account name</FieldLabel>
                    <input id="manual-name" type="text" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="e.g. Main checking" className="input-editorial max-w-xs" />
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <FieldLabel htmlFor="manual-type">Type</FieldLabel>
                      <select id="manual-type" value={manualType} onChange={(e) => setManualType(e.target.value)} className="select-editorial">
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <FieldLabel htmlFor="manual-balance">Current balance ($)</FieldLabel>
                      <input id="manual-balance" type="number" min="0" step="0.01" value={manualBalance} onChange={(e) => setManualBalance(e.target.value)} placeholder="0" className="input-editorial figure w-32" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={manualSaving} className="btn-primary">{manualSaving ? 'Adding…' : 'Add account'}</button>
                    <button type="button" onClick={() => { setShowManualForm(false); setAccountsMessage(''); }} className="btn-ghost">Cancel</button>
                  </div>
                </form>
              )}
              </SettingsBlock>
            </SettingsGroup>

            <SettingsGroup
              title="Currency"
              description="All figures in Aura will display in your chosen currency."
              icon={BanknotesIcon}
            >
              <SettingsBlock>
                <FieldLabel htmlFor="currency-select">Display currency</FieldLabel>
                <select
                  id="currency-select"
                  value={currency.code}
                  onChange={async (e) => {
                    const code = e.target.value;
                    try {
                      await setCurrency(code);
                      setCurrencySaved(true);
                      showToast?.('Currency updated', 'success');
                      setTimeout(() => setCurrencySaved(false), 2000);
                    } catch (err) {
                      console.error(err);
                      showToast?.('Could not update currency', 'error');
                    }
                  }}
                  className="select-editorial max-w-md"
                >
                  {SUPPORTED_CURRENCY_LIST.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.symbol} {c.code} — {c.name}
                    </option>
                  ))}
                </select>
                {currencySaved && (
                  <p className="text-label text-pine mt-2">Saved</p>
                )}
              </SettingsBlock>
            </SettingsGroup>

            <SettingsGroup
              title="Notifications"
              description="Email newsletters and account snapshot."
              icon={BellIcon}
            >
              <SettingsBlock>
                <LockedEmailSection
                  title="Email notifications"
                  description="Newsletters and account snapshot emails are temporarily unavailable."
                >
                  <p className="text-label text-secondary mb-3">Newsletter frequency</p>
                  <div className="space-y-2">
                    {newsletterOptions.map((option) => (
                      <label key={option.value} className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="newsletter_frequency"
                          value={option.value}
                          checked={newsletterFrequency === option.value}
                          disabled
                          readOnly
                          className="radio-editorial"
                        />
                        <span className="text-body text-ink-muted">{option.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-hairline">
                    <p className="text-body text-ink-muted mb-3">
                      Email a complete snapshot of your Aura account: visions, community, learning,
                      cashflow, and activity.
                    </p>
                    <button type="button" disabled className="btn-secondary flex items-center gap-2">
                      Send account snapshot email
                    </button>
                  </div>
                </LockedEmailSection>
              </SettingsBlock>
            </SettingsGroup>

            <EngagementSettings />

            <div className="flex flex-wrap items-center justify-between gap-4 pt-8 mt-8 border-t border-hairline">
              {message && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-md border ${
                  message.includes('success') ? 'border-pine bg-pine-tint text-pine' : 'border-danger/40 text-danger'
                }`}>
                  {message.includes('success') ? <CheckIcon className="w-4 h-4" /> : <XMarkIcon className="w-4 h-4" />}
                  <span className="text-label">{message}</span>
                </div>
              )}
              <button type="button" onClick={handleSave} disabled={isLoading} className="btn-primary ml-auto">
                {isLoading ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>

      {disconnectTarget && (
        <DisconnectAccountModal
          account={disconnectTarget}
          onClose={() => !disconnectingId && setDisconnectTarget(null)}
          onConfirm={handleConfirmDisconnect}
          isDisconnecting={disconnectingId === disconnectTarget.account_id}
        />
      )}

      {/* AI Avatar Generation Modal */}
      <AvatarPreferenceModal
        isOpen={showAIAvatarModal}
        onClose={() => setShowAIAvatarModal(false)}
        displayName={displayName}
      />
    </div>
  );
};

export default AccountSettings;
